// Import React/Redux.
import * as React from 'react';
import _ from 'lodash';

// Import internal components.
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { JsonSchemaValidator } from "@Utils/JsonSchemaValidator";
import { WebNode } from '@API/WebNode';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { isDefined, IsNullOrEmpty, isStringEmpty } from '@Utils/Micro';
import { getIconClassName } from '@Primitive/IconSelectorUtils';
import { LabelAutoSize } from '@Primitive/Typography';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { SettingsList } from '@Primitive/SettingsList';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { Plegma } from '@API/Api';
// =================================================================================================
const enum StatTilePortKeys {
    Data = "data",
    OnClick = "onClick"
}
// -------------------------------------------------------------------------------------------------
export interface StatTileData {
    BackgroundColor: string;
    TextColor: string;
    Title: string;
    TitleIcon: string;
    Body: string;
    BodyIcon: string;
    Compare: string;
    CompareIcon: string;
    IsClicable: boolean;
}
// -------------------------------------------------------------------------------------------------
export interface StatTileState {
    Data: StatTileData;
    /**
     * Keep keys for labels which can be localized and their
     * value can also be set from a port event. On port event 
     * remove the referenced labels from localization.
     */
    LocalizableLabels: string[];
}
// -------------------------------------------------------------------------------------------------
export class StatTile extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<StatTileData>, StatTileState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<StatTileData>) {
        super(props);
        this.state = {
            Data: _.cloneDeep(this.props.WidgetData),
            LocalizableLabels: this.initLocalizableLabels
        };
    }
    // ---------------------------------------------------------------------------------------------------
    get initLocalizableLabels(): string[] {
        // Initially all labels can be localized.
        return [
            LocalizationLabelKey.title,
            LocalizationLabelKey.body,
            LocalizationLabelKey.compare
        ];
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        // Check for initial localization.
        this._localizeLabels(this.props.LocalizationMap);

        if (_.has(this.props.PortsStates, StatTilePortKeys.Data)) {
            let port = this.props.PortsStates[StatTilePortKeys.Data];
            if (port.LastStateStatus == StateStatus.Valid)
                this.updateData(port.State);
        }
    }
    // ---------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<StatTileData>) {
        // Widget's data changed?
        if (!_.isEqual(this.props.WidgetData, prevProps.WidgetData))
            this.setState({
                Data: _.cloneDeep(this.props.WidgetData),
                LocalizableLabels: this.initLocalizableLabels
            });

        // Localization map changed?
        if (!_.isEqual(this.props.LocalizationMap, prevProps.LocalizationMap))
            this._localizeLabels(this.props.LocalizationMap);

        // Update data
        if (this.props.PortVersion != prevProps.PortVersion &&
            _.has(this.props.PortsStates, StatTilePortKeys.Data)) {
            let port = this.props.PortsStates[StatTilePortKeys.Data];
            if (port.LastStateStatus == StateStatus.Valid)
                this.updateData(port.State);
        }
    }
    // ---------------------------------------------------------------------------------------------
    updateData(data: StatTileData) {
        this.setState(s => {
            _.forOwn(s.Data, (v, k) => {
                if (!_.isEmpty(data[k])) {
                    s.Data[k] = data[k];

                    // Remove label from localization.
                    if (_.indexOf(s.LocalizableLabels, k) > -1)
                        _.pull(s.LocalizableLabels, k);
                }
            });
            return s;
        });
    }
    // ---------------------------------------------------------------------------------------------
    private _localizeLabels(localizationMap: LocalizationMap) {
        // Get labels available for localization.
        const labels = this
            .state
            .LocalizableLabels;

        // Can we localize anything?
        if (labels.length > 0) {
            this.setState((s: StatTileState) => {
                _.forEach(labels, (v: string) => {
                    // Try to localize the label.
                    const value = localizationMap[v];
                    if (!isStringEmpty(value))
                        s.Data[v] = value;
                });
                return s;
            });
        }
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let itemHeight = Math.round((this.props.height - 40) / 3);
        let itemWidth = this.props.width - 20;
        return <div style={{
            width: this.props.width,
            height: this.props.height,
            backgroundColor: this.state.Data.BackgroundColor,
            color: this.state.Data.TextColor,
            padding: "10px 14px",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            display: "flex"
        }}>
            <LabelAutoSize
                Text={<React.Fragment>
                    <i style={{ paddingRight: 8 }} className={getIconClassName(this.state.Data.TitleIcon)} >
                    </i>
                    {this.state.Data.Title}
                </React.Fragment>}
                Height={itemHeight}
                Width={itemWidth}
                MaxFontSize={20}
            />
            <LabelAutoSize
                Text={this.state.Data.Body}
                Height={itemHeight}
                Width={itemWidth}
                MaxFontSize={24}
                Style={{ fontWeight: 500, margin: "0.2rem 0 0.2rem 0" }}
                MultiLine={false}
            />
            <div
                style={{ display: "flex", fontSize: "1.4rem", margin: 0, cursor: ((!this.props.WidgetData.IsClicable) ? undefined : "pointer") }}
                onClick={(!this.props.WidgetData.IsClicable) ? undefined : () => this.props.SendPortEvent(StatTilePortKeys.OnClick, true)}
                onMouseEnter={(!this.props.WidgetData.IsClicable) ? undefined : (event: React.MouseEvent<HTMLDivElement>) => {
                    const element: HTMLDivElement = event.target as HTMLDivElement;
                    element.style.color = 'white';
                    element.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(!this.props.WidgetData.IsClicable) ? undefined : (event: React.MouseEvent<HTMLDivElement>) => {
                    const element: HTMLDivElement = event.target as HTMLDivElement;
                    element.style.color = '';
                    element.style.backgroundColor = '';
                }}
            >
                <LabelAutoSize
                    Text={<React.Fragment>
                        <i style={{ paddingRight: 8 }} className={getIconClassName(this.state.Data.CompareIcon)} >
                        </i>
                        {this.state.Data.Compare}
                    </React.Fragment>}
                    Height={itemHeight}
                    Width={itemWidth}
                    MaxFontSize={18}
                />
            </div>
        </div>
    }
}
// =================================================================================================
export interface StatTileSettingsState {
    WidgetData: StatTileData;
}
// ---------------------------------------------------------------------------------------------
class StatTileSettings extends React.Component<IWidgetSettingsProps<StatTileData>, StatTileSettingsState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<StatTileData>) {
        super(props);

        // Initialize component's state.
        this.state = { WidgetData: _.cloneDeep(this.props.WidgetData) };
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<StatTileData>[] = [
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        tooltip: "Sets the widget's background color.",
                        dataPath: "BackgroundColor",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Text Color',
                        tooltip: "Sets the widget's text color.",
                        dataPath: "TextColor",
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Title Icon",
                        tooltip: "Sets the icon.",
                        dataPath: "TitleIcon",
                    },
                    {
                        type: SettingsType.String,
                        label: 'Title',
                        tooltip: "Sets the title.",
                        dataPath: "Title",
                    },
                    {
                        type: SettingsType.String,
                        label: 'Body',
                        tooltip: "Sets the body text.",
                        dataPath: "Body",
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Compare Icon",
                        tooltip: "Sets the icon of the compare field.",
                        dataPath: "CompareIcon",
                    },
                    {
                        type: SettingsType.String,
                        label: 'Compare',
                        tooltip: "Sets the text of the compare field.",
                        dataPath: "Compare",
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Is Clickable',
                        tooltip: 'Whether is clickable or not.',
                        dataPath: "IsClicable",
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                this.setState({ WidgetData: data },
                    () => {
                        if (isDefined(this.props.onWidgetDataUpdate))
                            this.props.onWidgetDataUpdate(this.state.WidgetData);
                    }
                )
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ---------------------------------------------------------------------------------------------
}
// =================================================================================================
/**
 * The keys are the same as in StatTileData interface.
 */
const enum LocalizationLabelKey {
    title = 'Title',
    body = 'Body',
    compare = 'Compare'
}
// ---------------------------------------------------------------------------------------------
export class StatTileWidget extends IWidgetThingsClass<StatTileData> {
    // ---------------------------------------------------------------------------------------------
    Render(key: string, width: number, height: number): React.ReactNode {
        return <StatTile />;
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <StatTileSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        return JsonSchemaValidator.GetInstance(
            "StatTileSchemeValidator",
            {
                type: "object",
                properties: {
                    BackgroundColor: {
                        type: "string"
                    },
                    TextColor: {
                        type: "string"
                    },
                    Title: {
                        type: ["string", "number"]
                    },
                    TitleIcon: {
                        type: "string"
                    },
                    Body: {
                        type: ["string", "number"]
                    },
                    BodyIcon: {
                        type: "string"
                    },
                    Compare: {
                        type: ["string", "number"]
                    },
                    CompareIcon: {
                        type: "string"
                    },
                }
            }).DataValidateString(value);
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: StatTileData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort(
            {
                Name: 'Data',
                PortKey: StatTilePortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'The tile data',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(StatTilePortKeys.Data, data)
        ));

        ports.push(new WebThingPort(
            {
                Name: 'OnClick',
                PortKey: StatTilePortKeys.OnClick,
                Type: Plegma.ePortType.Boolean,
                ioDirection: Plegma.ioPortDirection.Output,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'When the user click the body',
                State: ''
            },
            WebThingPortType.Trigger
        ));

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as StatTileData);

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): StatTileData {
        return {
            TextColor: "white",
            BackgroundColor: "#00bcd4",
            Title: "Total Stats",
            TitleIcon: "fa-star",
            Compare: "80% more from yesterday",
            CompareIcon: "fa-angle-up",
            Body: "$1900",
            BodyIcon: "",
            IsClicable: false
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return StatTileWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Stat Tile',
            WidgetTitle: '',
            Key: 'StatTile',
            Type: 'StatTileWidget',
            Data: StatTileWidget.GetDefaultWidgetDataStatic(),
            Description: 'Displays basic info, highlights numbers or stats.',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    GetLocalizationDictionary(): LocalizationMap {
        const res = {};

        // add widget's title
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        // add `Title` if defined
        if (!IsNullOrEmpty(this.WidgetData.Title))
            res[LocalizationLabelKey.title] = this.WidgetData.Title;

        // add `Body` if defined
        if (!IsNullOrEmpty(this.WidgetData.Body))
            res[LocalizationLabelKey.body] = this.WidgetData.Body;

        // add `Compare` if defined
        if (!IsNullOrEmpty(this.WidgetData.Compare))
            res[LocalizationLabelKey.compare] = this.WidgetData.Compare;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    GetDataPortKeyStatic(): string { return StatTilePortKeys.Data; };
    // ------------------------------------------------------------------------------------------------
}